/**
 * AppUpdateWatcher — makes a new deploy reach open tabs and returning
 * visitors without anyone clearing their browser cache.
 *
 * The server sends index.html without a Cache-Control header, so a browser
 * can keep reusing an old copy for up to a day, and a tab that stays open
 * never loads new code at all. This component asks the server for the
 * current index.html (bypassing the HTTP cache) when the tab comes back into
 * view, when the window regains focus, and every few minutes. If it names a
 * different main bundle than the one running, the next in-app navigation
 * does a full page load. Reloading on navigation, never mid-page, means
 * nobody loses what they are typing.
 *
 * Loop guard: the bundle a reload was attempted for is kept in
 * sessionStorage. If the page comes back on the old bundle anyway (a proxy
 * or an aggressive cache), we do not try again for that same bundle.
 *
 * Renders nothing. Does nothing on the dev server, where index.html has no
 * hashed bundle.
 */
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

const BUNDLE_RE = /\/assets\/index-[\w-]+\.js/
const CHECK_EVERY_MS = 5 * 60 * 1000
const MIN_GAP_MS = 30 * 1000
const ATTEMPT_KEY = 'ovacs.updateAttempt'

function runningBundle() {
  const el = document.querySelector('script[type="module"][src*="/assets/index-"]')
  return (el?.getAttribute('src') || '').match(BUNDLE_RE)?.[0] || null
}

async function latestBundle() {
  const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' })
  if (!res.ok) return null
  return (await res.text()).match(BUNDLE_RE)?.[0] || null
}

function readAttempt() {
  try { return sessionStorage.getItem(ATTEMPT_KEY) } catch { return null }
}

function writeAttempt(value) {
  try {
    if (value) sessionStorage.setItem(ATTEMPT_KEY, value)
    else sessionStorage.removeItem(ATTEMPT_KEY)
  } catch { /* storage blocked: the loop guard is best effort */ }
}

export default function AppUpdateWatcher() {
  const { pathname } = useLocation()
  const running = useRef(runningBundle())
  const pending = useRef(null)
  const lastCheck = useRef(0)
  const firstPath = useRef(true)

  useEffect(() => {
    if (!running.current) return undefined
    // A reload for this bundle succeeded: forget the attempt.
    if (readAttempt() === running.current) writeAttempt(null)

    const check = async () => {
      const now = Date.now()
      if (pending.current || now - lastCheck.current < MIN_GAP_MS) return
      lastCheck.current = now
      try {
        const latest = await latestBundle()
        if (latest && latest !== running.current && latest !== readAttempt()) {
          pending.current = latest
        }
      } catch { /* offline or server busy: the next check tries again */ }
    }
    const onVisibility = () => { if (document.visibilityState === 'visible') check() }

    check()
    const timer = setInterval(check, CHECK_EVERY_MS)
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [])

  // Load the new build on the next page change after it was detected.
  useEffect(() => {
    if (firstPath.current) { firstPath.current = false; return }
    if (!pending.current) return
    writeAttempt(pending.current)
    window.location.reload()
  }, [pathname])

  return null
}
